import { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../lib/api";
import { SiteScorecards } from "./hs/InspectionsTab";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ShieldCheck, EnvelopeSimple, Eye, PaperPlaneTilt } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "");

/** Monthly safety e-mail status for this client: who gets it, what last month's edition contains, when it last went out; preview + send now. */
function MonthlyEmailRow({ clientId, canSend, onSent }) {
  const [st, setSt] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => API.get("/hse/client-monthly/preview", { params: { client_id: clientId } }).then((r) => setSt(r.data)).catch(() => setSt({ recipients: [], counts: {} })), [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const h = () => load(); window.addEventListener("portal-users-changed", h); return () => window.removeEventListener("portal-users-changed", h); }, [load]);
  if (!st) return null;
  const n = st.recipients?.length || 0;
  const c = st.counts || {};
  const send = async () => {
    setBusy(true);
    try { const { data } = await API.post("/scheduler/hse-client-monthly/run-now", { client_id: clientId, force: true }); toast.success(data.message); load(); onSent?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-t border-border bg-muted/10 text-xs" data-testid="client-monthly-email">
      <EnvelopeSimple size={14} className="text-primary shrink-0" />
      <span className="font-medium">Monthly safety e-mail</span>
      <span className="text-muted-foreground" data-testid="client-monthly-recipients">
        {n === 0 ? <>No safety contacts yet — tick <strong>Safety e-mails</strong> on a portal user above to switch it on.</> : <>{n} safety contact{n === 1 ? "" : "s"} · 1st of every month · {st.month_label}: {c.inspections || 0} inspection{c.inspections === 1 ? "" : "s"}, {c.bulletins || 0} bulletin{c.bulletins === 1 ? "" : "s"}, {c.attachments || 0} site PDF{c.attachments === 1 ? "" : "s"} attached</>}
      </span>
      {st.last_sent?.sent_at && <span className="text-muted-foreground" data-testid="client-monthly-last-sent">· last sent {fmt(st.last_sent.sent_at)} ({st.last_sent.emails} e-mail{st.last_sent.emails === 1 ? "" : "s"})</span>}
      <span className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setPreview(true)} data-testid="client-monthly-preview-btn"><Eye size={12} className="mr-1" /> Preview</Button>
        {canSend && <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy || n === 0 || !st.has_content} title={n === 0 ? "Tick a safety contact first" : !st.has_content ? "Nothing shared with this client yet" : "Send this month's e-mail now"} onClick={send} data-testid="client-monthly-send-btn"><PaperPlaneTilt size={12} className="mr-1" /> {busy ? "Sending…" : "Send now"}</Button>}
      </span>
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-3xl" data-testid="client-monthly-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Monthly safety e-mail — {st.month_label}</DialogTitle>
            <DialogDescription>{n ? `Goes to ${st.recipients.map((r) => r.name || r.email).join(", ")}` : "No safety contacts ticked yet"} · {st.schedule}{c.attachments ? ` · ${c.attachments} site report PDF${c.attachments === 1 ? "" : "s"} attached` : ""}</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border bg-white max-h-[65vh] overflow-auto p-4 text-slate-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(st.html || "", { USE_PROFILES: { html: true }, FORBID_TAGS: ["style", "form", "input", "iframe", "script"] }) }} data-testid="client-monthly-preview-html" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Office view on a client's record: per-site safety scorecards with a one-click Site report PDF, plus the monthly safety e-mail. */
export default function ClientSafetySitesPanel({ clientId, canSend = true }) {
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState(0);
  useEffect(() => {
    API.get("/hse/inspections/sites", { params: { client_id: clientId } }).then((r) => setData(r.data)).catch(() => setData({ sites: [] }));
    const users = () => API.get(`/clients/${clientId}/portal-users`).then((r) => setContacts((r.data.users || []).filter((u) => u.safety_contact).length)).catch(() => setContacts(0));
    users();
    window.addEventListener("portal-users-changed", users);
    return () => window.removeEventListener("portal-users-changed", users);
  }, [clientId]);
  if (!data) return null;
  const sites = data.sites || [];
  if (!sites.length && !contacts) return null;
  return (
    <div className="mb-6 border border-border rounded-lg overflow-hidden" data-testid="client-safety-sites">
      <div className="flex items-center justify-between p-4 bg-muted/20 border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-emerald-600" />
          <h2 className="text-sm font-semibold font-[Chivo]">Site safety scorecards</h2>
          <span className="text-[11px] text-muted-foreground">{sites.length} site{sites.length === 1 ? "" : "s"} inspected · Site report = per-site PDF for the building manager</span>
        </div>
        <Link to="/health-safety?tab=inspections" className="text-xs text-primary hover:underline" data-testid="client-safety-sites-link">Open inspections</Link>
      </div>
      {sites.length > 0 ? <div className="p-4"><SiteScorecards rows={sites} compact clientId={clientId} /></div> : <p className="px-4 py-3 text-xs text-muted-foreground" data-testid="client-safety-sites-empty">No completed inspections at this client's sites yet.</p>}
      <MonthlyEmailRow clientId={clientId} canSend={canSend} />
    </div>
  );
}
